import { CommonModule, TitleCasePipe } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    Renderer2,
    SimpleChanges,
    inject
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { ProductComponentDef } from '@df/catalog-ui/product/product.component.def';
import { ICategoryDataV3 } from '@df/catalog/category/category';
import { IProductDataV2, IProductDataV3 } from '@df/catalog/product/product';
import { AbModule } from '@df/module-ab/ab.module';
import { GtmImpressionTrackDirective } from '@df/module-gtm/gtm-impression-track.directive';
import { GtmTrackProductClickDirective } from '@df/module-gtm/gtm-track-product-click.directive';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { ImageModule } from '@df/module-image/image.module';
import { KlevuSearch } from '@df/module-klevu-search/klevu-interfaces';
import { KlevuTrackCategoryProductClickDirective } from '@df/module-klevu-search/klevu-track-category-product-click.directive';
import { KlevuTrackClickDirective } from '@df/module-klevu-search/klevu-track-click.directive';
import { KlevuTrackRecommendationsProductClickDirective } from '@df/module-klevu-search/klevu-track-recommendations-product-click.directive';
import { findMediaByLabel } from '@df/ui/common/find-media-by-label.pipe';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { OptionColorPipe } from '@df/ui/common/option-color.pipe';
import { OptionLabelPipe, optionLabelPipe } from '@df/ui/common/option-label.pipe';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import flattenDeep from 'lodash-es/flattenDeep';
import take from 'lodash-es/take';
import { Subscription } from 'rxjs';
import { BasketItemQtyComponent } from '../../../basket/item-qty/basket-item-qty.component';
import { ReviewsIORatingComponent } from '../../../module/reviews-io/components/reviews-io-rating.component';
import { FinanceFromPlpComponent } from '../../../module/v12-retail-finance/v12-retail-finance-from-plp.component';
import { AltImageComponent } from '../../../ui/display/alt-image/alt-image.component';
import { ShellComponent } from '../../../ui/display/local-shell.component';
import { ProductListing } from '../../../ui/listing/product-listing.decorator';
import { SwatchComponent } from '../../attribute/swatch/swatch.component';
import { Category } from '../../category/category.decorator';
import { EProductBannerType, IProductConfigFamilyMapItem, IProductCrossSellFamilyMapItem, Product } from '../../product/product.decorator';
import { ProductService } from '../product.service';
import { QuickAddToBasketComponent } from '../quick-add-to-basket/quick-add-to-basket.component';
import { ProductWishlistToggleComponent } from '../wishlist-toggle/product-wishlist-toggle.component';
import { EListingLabelOptionId, IProductLabel } from './../product.decorator';
const PROMOTION_BANNERS_BLACK_FRIDAY_IDS = [38238, 38239];

@Component({
    selector: 'product',
    templateUrl: 'product.component.html',
    styleUrls: ['product.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        GtmTrackProductClickDirective,
        KlevuTrackClickDirective,
        KlevuTrackRecommendationsProductClickDirective,
        KlevuTrackCategoryProductClickDirective,
        CommonModule,
        RouterLink,
        SwatchComponent,
        HrefComponent,
        OptionLabelPipe,
        TranslationModule,
        OptionColorPipe,
        PriceComponent,
        ProductWishlistToggleComponent,
        ImageModule,
        ShellComponent,
        GtmImpressionTrackDirective,
        AltImageComponent,
        CommonModule,
        SizeClassDirective,
        AbModule,
        TitleCasePipe,
        ReviewsIORatingComponent,
        BasketItemQtyComponent,
        QuickAddToBasketComponent,
        FinanceFromPlpComponent
    ]
})
export class ProductComponent extends ProductComponentDef<Product<IProductDataV2>> implements AfterViewInit, OnChanges, OnDestroy {
    modelLabels: IProductLabel[] = [];
    @Input()
    gridSize?: number;
    @Input()
    sendTracking?: boolean;
    @Input()
    hideSwatches = false;
    @Input()
    hideDetails = false;
    @Input()
    hideBrandRange = false;
    @Input()
    hideLabels = false;
    @Input()
    hideWishlist = false;
    @Input()
    hideLastXRemainingBanner = false;
    @Input()
    hidePrice?: boolean;
    @Input()
    hideBorder?: boolean;
    @Input()
    showRangeCode = false;
    @Input()
    smallText = false;
    @Input()
    set quickAdd(value: boolean) {
        this.allowConfigurableChildrenLoad = this._quickAdd = value;
    }
    get quickAdd() {
        return this._quickAdd;
    }
    @Input()
    smallWishlist = false;
    @Input()
    category?: Category<ICategoryDataV3>;
    @Input()
    klevuRecommendationId?: string;

    @Input()
    klevuTypeId?: string;
    @Input()
    showReviews = false;

    @Input()
    override listing?: ProductListing<Product<IProductDataV2>>;

    /**
     * Min number of swatches
     */
    minSwatches = 2;
    selectedQty = 1;
    _quickAdd: boolean = false;
    currentSwatchMapItem?: IProductConfigFamilyMapItem | IProductCrossSellFamilyMapItem;
    swatchMapItems?: IProductConfigFamilyMapItem[] | IProductCrossSellFamilyMapItem[] | [];
    defaultCrossSellSwatchImage: boolean | undefined;
    currentLabels: IProductLabel[] = [];
    currentInStockLabel?: IProductLabel;
    /**
     * Image height for 2+ cols layout
     */
    private imageHeightStyle?: string;
    private hovered = false;
    showBaseImage = true;
    /**
     * Optional search term if showing from the search page
     */
    @Input()
    searchTerm?: string;
    private listingSubscription?: Subscription;

    private renderer2 = inject(Renderer2);

    private productService = inject(ProductService);

    ngAfterViewInit() {
        this.renderer2.setStyle(this.elementRef.nativeElement as HTMLElement, 'visibility', 'visible');
    }
    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes['gridSize'] && this.initiated) {
            this.detectChanges();
        }
        if (changes['listing']) {
            this.listingSubscription?.unsubscribe();
            if (this.listing) {
                this.listing.subscribe(() => this.onListingUpdate());
                this.onListingUpdate();
            }
        }
    }
    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.listingSubscription?.unsubscribe();
    }
    private onListingUpdate(): void {
        if (this.listing && this.swatchMapItems?.length) {
            const selection = this.listing.filters.find(i => i.config?.id === 'color_filter')?.selection;
            if (Array.isArray(selection) && selection.length) {
                let swatch: IProductConfigFamilyMapItem | undefined = undefined;
                selection.reverse().find(colorOption => {
                    swatch = this.swatchMapItems?.find(i => i['color_filter'] && i['color_filter'] === colorOption);
                    if (swatch) {
                        return true;
                    }
                });
                if (swatch) {
                    this.updateSwatch(swatch);
                }
            }
        }
    }
    override onModelChange(): void {
        super.onModelChange();
        this.modelLabels = take(this.model.getListingLabels(), 2);
        this.swatchMapItems = this.getConfigFamilyMapItems() || this.getCrossSellFamilyMapItems() || [];
        this.defaultCrossSellSwatchImage = !!(this.model.data?.img_swatch && this.getCrossSellFamilyMapItems());
        this.currentSwatchMapItem = this.swatchMapItems.find(i => i.default_variant) || this.swatchMapItems[0];
        this.currentLabels = this.labels;
        this.currentInStockLabel = this.currentLabels?.find(label => label.label === this.inStockLabel?.label);
    }
    toggleImages(to: boolean): void {
        if (this.getAlternativeImage()) {
            this.showBaseImage = to;
            this.detectChanges();
        }
    }
    /**
     * If display is a banner with text over image
     */
    isBannerWithTextOverImage(): boolean {
        if (this.model?.isBanner && this.model.data?.banner_type === EProductBannerType.textOnImage) {
            if (this.gridColumn == 2) {
                return true;
            } else if (this.gridSize == 2) {
                return false;
            }
            return true;
        }
        return false;
    }
    /**
     * Retrieves image padding = x = height/width * 100
     *
     * We used this rather then ratio to make thing match more accurately without creating new ratios
     */
    getImageHeightStyle(): string {
        if (this.isBannerWithTextOverImage() && this.gridColumn !== 2) {
            return '100%';
        } else if (!this.imageHeightStyle || !this.requiresSizeWatch()) {
            return `${this.get1x1Ratio() * 100}%`;
        }
        return this.imageHeightStyle;
    }
    get mobileBannerImage(): string | undefined {
        const media = (<IProductDataV3>this.model?.data)?.media || [];
        return findMediaByLabel(media, 'banner@s') || this.model?.data?.image;
    }
    /**
     * Gets product image for given layout
     */
    getImage(): string | undefined {
        if (this.model?.isBanner && this.client.isS) {
            return this.mobileBannerImage;
        }
        if (this.getLayout() === '2x1') {
            if (this.currentSwatchMapItem) {
                return this.currentSwatchMapItem.img_horizontal || this.currentSwatchMapItem.image;
            }
            return this.model.data?.img_horizontal || this.model.data?.image;
        }
        if (this.currentSwatchMapItem) {
            return this.currentSwatchMapItem.image;
        }
        return this.model.data?.image;
    }
    /**
     * Gets product image for given layout
     */
    getAlternativeImage(): string | undefined {
        if (this.getLayout() === '2x1') {
            return undefined;
        }
        return this.currentSwatchMapItem ? this.currentSwatchMapItem.img_alternative : this.model.data?.img_alternative;
    }
    get hasAlternativeImage(): boolean {
        return !!this.getAlternativeImage();
    }
    /**
     * If size watch is required
     */
    protected override requiresSizeWatch() {
        return this.getLayout() !== '1x1';
    }
    /**
     * On component size change callback
     */
    protected override onSizeChange(dims: { height: number; width: number }) {
        this.imageHeightStyle =
            typeof this.gridColumn !== 'undefined'
                ? this.listing?.plugin.grid.getImageHeight(this.get1x1Ratio(), this.gridColumn, dims.width)
                : undefined;
        this.detectChanges();
    }
    /**
     * Returns image ration for 1x1 column dependin on product type
     */
    private get1x1Ratio(): number {
        return this.isBannerWithTextOverImage() ? 1.25 : 1;
    }
    /**
     * on mobile we always use 1x1 layout as we have up to 2 cols in grid
     * and 2-col item take a whole row then and keeps the original ratio
     */
    override getLayout(): string {
        if (this.client.isS) {
            return '1x1';
        }
        return super.getLayout();
    }
    /////////////////////////////////////
    /////
    /////   Alt image
    /////
    /////////////////////////////////////
    onMouseEnter() {
        if (!this.hovered) {
            this.hovered = true;
            this.detectChanges();
        }
    }
    onMouseOut() {
        if (this.hovered) {
            this.hovered = false;
            this.detectChanges();
        }
    }
    /**
     * Alternative image is only printed after first time it was needed
     */
    canPrintAltImage(): boolean {
        return !this.client.isTouchy && !!(this.hovered && this.getAlternativeImage());
    }
    private getConfigFamilyMapItems(): IProductConfigFamilyMapItem[] | undefined {
        if (this.model?.isConfigurable && this.model?.data?.configFamilyMap?.length) {
            const stockMap = this.model.data.stockMap || {};
            const inStockChildren: number[] = flattenDeep(Object.values(stockMap).map(i => Object.values(i)));

            return this.model.data.configFamilyMap.filter(i => i.url && i.id && inStockChildren.indexOf(i.id) > -1);
        }
        return undefined;
    }
    private getCrossSellFamilyMapItems(): IProductCrossSellFamilyMapItem[] | undefined {
        if (this.model?.isSimple && this.model?.data?.crossSellFamilyMap?.length && !this.model?.isOutOfStock) {
            const crossSellItems = this.model.data.crossSellFamilyMap.filter(
                i => i.url && i.id && i.config_colour && !i.config_size && ((i.qty || 0) > 0 || (i.backorders_qty || 0) > 0)
            );

            if (crossSellItems.length) {
                return [this.model.data, ...crossSellItems];
            }
        }
        return undefined;
    }
    get url(): string | undefined {
        if (this.currentSwatchMapItem) {
            return this.currentSwatchMapItem?.url;
        }
        return this.model?.data?.url;
    }
    get name(): string | undefined {
        if (this.currentSwatchMapItem) {
            return this.currentSwatchMapItem?.name;
        }
        return this.model?.data?.name;
    }
    get rangeName(): string | undefined {
        if (this.currentSwatchMapItem) {
            return this.currentSwatchMapItem.range_name;
        }
        return this.model?.data?.range_name;
    }
    get productOutline(): string | undefined {
        if (this.currentSwatchMapItem) {
            return this.currentSwatchMapItem.product_outline;
        }
        return this.model?.data?.product_outline;
    }
    get displayFromPrice(): boolean {
        if (!this.currentSwatchMapItem) {
            return !!this.model?.data?.is_from_price;
        }
        return false;
    }
    override get price(): number {
        if (this.currentSwatchMapItem) {
            return this.currentSwatchMapItem.price || 0;
        }
        return this.model?.price;
    }
    override get orgPrice(): number | undefined {
        if (this.currentSwatchMapItem) {
            return this.currentSwatchMapItem.org_price;
        }
        return this.model?.orgPrice;
    }
    get inStockLabel(): IProductLabel | undefined {
        const inStockLabel = this.productService.getListingLabelProductLabel(EListingLabelOptionId.inStock);
        return this.modelLabels.find(label => label.label === inStockLabel?.label);
    }
    onSwatchMouseEnter(swatch: IProductConfigFamilyMapItem): void {
        this.updateSwatch(swatch);
    }
    private updateSwatch(swatch: IProductConfigFamilyMapItem): void {
        if (this.currentSwatchMapItem !== swatch) {
            this.currentSwatchMapItem = swatch;
            this.currentLabels = this.labels;
            this.currentInStockLabel = this.currentLabels?.find(label => label.label === this.inStockLabel?.label);
            this.detectChanges();
        }
    }
    get labels(): IProductLabel[] {
        if (
            (this.swatchMapItems?.length && this.currentSwatchMapItem?.qty) ||
            (this.currentSwatchMapItem?.id === this.model?.id && this.inStockLabel) ||
            !this.swatchMapItems?.length
        ) {
            return this.modelLabels;
        } else {
            return this.modelLabels?.filter(label => label.label !== this.inStockLabel?.label);
        }
    }
    get wishlistId(): number | undefined {
        if (this.currentSwatchMapItem) {
            return this.currentSwatchMapItem.id;
        }
        return this.model?.id;
    }
    get gtmProductImpression(): Gtm.ITrackImpressionData | undefined {
        if (this.sendTracking && this.localGtmProductClick?.productId) {
            return { ...this.localGtmProductClick, threshold: 10 };
        }
        return undefined;
    }
    get localGtmProductClick(): Gtm.ITrackProductClickData | undefined {
        if (this.sendTracking) {
            return super.gtmProductClick;
        }
        return undefined;
    }
    get klevuSearchTrackClick(): KlevuSearch.ITrackSearchProductClickData | undefined {
        if (this.searchTerm) {
            return {
                product: this.model,
                term: this.searchTerm
            };
        }
        return undefined;
    }
    get klevuCategoryProductTrackClick(): KlevuSearch.ITrackCategoryProductClickData | undefined {
        if (this.sendTracking && this.category) {
            return {
                product: this.model,
                category: this.category,
                position: (this.listPosition || 0) > 0 ? (this.listPosition || 0) - 1 : undefined
            };
        }
    }
    get klevuRecommendationsProductClickData():
        | KlevuSearch.IAnalyticsRecommendationTrackData<KlevuSearch.EAnalyticsRecommendationEvent.select>
        | undefined {
        if (this.klevuRecommendationId && this.model && typeof this.listPosition === 'number') {
            return <KlevuSearch.IAnalyticsRecommendationTrackData<KlevuSearch.EAnalyticsRecommendationEvent.select>>{
                event: KlevuSearch.EAnalyticsRecommendationEvent.select,
                eventListId: this.klevuRecommendationId,
                products: [{ model: this.model, position: this.listPosition }]
            };
        }
        return undefined;
    }

    get isBlackLabel(): boolean {
        return !!this.model.data?.promotion_banner && PROMOTION_BANNERS_BLACK_FRIDAY_IDS.includes(this.model.data.promotion_banner);
    }

    get maxQty(): number {
        if (this.model) {
            return Math.max(this.model.getStockQty(true, undefined, false), 0);
        }
        return 0;
    }

    get isLastXRemainingBanner(): boolean {
        const lastXRemainingBannerIds = [41467, 39750, 47598];
        const promoBanner = this.model.data?.PLP_promo_banner;
        return !!(promoBanner && lastXRemainingBannerIds.includes(promoBanner));
    }

    get bannerBackgroundColor(): string | undefined {
        const plpPromoBannerLabel = optionLabelPipe(this.model.data?.PLP_promo_banner, 'PLP_promo_banner');

        const plpPromoBannerColourAttribute = this.app.resources.attribute.get('PLP_promo_banner_colour');

        return plpPromoBannerColourAttribute.data?.options?.find(option => option.label === plpPromoBannerLabel)?.swatch;
    }

    get isMaxQty(): boolean {
        return this.maxQty > 0 && this.maxQty < 10;
    }

    asString(input: string | number | undefined) {
        return input as string;
    }

    get addToBasketProduct() {
        return this.currentSwatchMapItem?.id ? this.app.resources.product.get(this.currentSwatchMapItem.id as number) : this.model;
    }
}
